import { useCallback, useEffect, useState } from "react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { API_BASE, api } from "../../api/client";
import type { ClientMe, ClientQuote, ClientQuoteList } from "../../api/types";
import { formatMoney } from "../../lib/money";

// 客戶那一側的整個畫面(第 14 輪)。
//
// 業主原話:「客戶透過連結 登入系統時 輸入他的統編/mail/密碼
// 密碼在信中要給臨時密碼 他近來時強制請他自訂」。
//
// --------------------------------------------------------------------------
// 這一頁**完全不在 AuthProvider 底下**
// --------------------------------------------------------------------------
// 它掛在 `/client`,與 `/login`、`/forgot`、`/reset` 同一層(公開路由)。
//
// 包進 `RequireAuth` 的話,客戶會被導去員工的登入頁 —— 而他沒有員工帳號,
// 那一頁對他來說是一面永遠過不去的牆。
//
// 後端那一側也是分開的:另一張表、另一個 cookie 名稱、另一個簽章 salt。
//
// --------------------------------------------------------------------------
// 強制改密碼是**擋住**,不是提示
// --------------------------------------------------------------------------
// 提示是可以按掉的,而按掉之後那組寄在 Email 明文裡的密碼就會一直有效。
// 後端用 409 擋住其他每一支端點;這裡則是根本不畫別的東西出來。

/** 金額一律走 decimal.js。 */
function money(value: string | number | null | undefined): string {
  // 全系統金額一律 0,000,000、四捨五入到整數(業主 2026-10-15)
  return formatMoney(value);
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--bg)] px-4 py-8">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
        <h1 className="m-0 text-xl font-semibold text-[var(--text)]">星采設計 · 報價單確認</h1>
        {children}
      </div>
    </div>
  );
}

export function ClientPortal() {
  const [me, setMe] = useState<ClientMe | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api
      .get<ClientMe>("/client/me")
      .then((got) => setMe(got && typeof got === "object" && "email" in got ? got : null))
      .catch(() => setMe(null))
      .finally(() => setChecked(true));
  }, []);

  if (!checked) {
    return (
      <Shell>
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      </Shell>
    );
  }
  if (me === null) return <ClientLogin onIn={setMe} />;
  // 見檔頭:還在用臨時密碼的人,這裡**只畫得出改密碼那一張表**。
  if (me.must_change_password) return <ForcePassword me={me} onDone={setMe} />;
  return <ClientQuotes me={me} onOut={() => setMe(null)} />;
}

function ClientLogin({ onIn }: { onIn: (me: ClientMe) => void }) {
  const [taxId, setTaxId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onIn(await api.post<ClientMe>("/client/login", { tax_id: taxId, email, password }));
    } catch (err) {
      // 後端的原句照顯示 —— 它分得出「密碼不對」與「臨時密碼過期了」,
      // 而後者要告訴客戶的下一步(請承辦同仁重寄)前端猜不出來。
      setError(err instanceof Error ? err.message : "登入失敗");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <form
        onSubmit={submit}
        aria-label="客戶登入"
        className="flex flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
      >
        <p className="m-0 text-sm text-[var(--text-2)]">
          請輸入信件中的三項資料。第一次登入之後會請您改成自己的密碼。
        </p>
        <Input label="統一編號" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input
          label="密碼"
          type="password"
          hint="第一次登入請用信件中的臨時密碼"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" loading={busy} disabled={!taxId || !email || !password}>
          登入
        </Button>
      </form>
    </Shell>
  );
}

function ForcePassword({ me, onDone }: { me: ClientMe; onDone: (me: ClientMe) => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = next !== "" && again !== "" && next !== again;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(
        await api.post<ClientMe>("/client/password", {
          current_password: current,
          new_password: next,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "改不了");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <form
        onSubmit={submit}
        aria-label="設定你的密碼"
        className="flex flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
      >
        <p className="m-0 text-sm text-[var(--text-2)]">
          {me.company_name} · {me.email}
        </p>
        <p className="m-0 text-sm text-[var(--text-2)]">
          信件裡那組臨時密碼是明文寄出的,<strong>請先改成只有您知道的密碼</strong>,才能繼續。
        </p>
        <Input
          label="信件中的臨時密碼"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="新密碼"
          type="password"
          hint="至少 8 個字"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <Input
          label="再打一次"
          type="password"
          /* 兩次不一樣要當場說,不是等按下去才說 —— 打錯的人自己看不出來。 */
          error={mismatch ? "兩次輸入的新密碼不一樣" : null}
          errorRole="alert"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
        />
        {error ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={!current || next.length < 8 || mismatch}
        >
          設定密碼並繼續
        </Button>
      </form>
    </Shell>
  );
}

function ClientQuotes({ me, onOut }: { me: ClientMe; onOut: () => void }) {
  const [rows, setRows] = useState<ClientQuote[]>([]);
  const [signing, setSigning] = useState<ClientQuote | null>(null);

  const refresh = useCallback(() => {
    api
      .get<ClientQuoteList>("/client/quotes")
      .then((got) => setRows(got && Array.isArray(got.items) ? got.items : []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = async () => {
    try {
      await api.post("/client/logout", {});
    } catch {
      /* 登出失敗也要讓畫面回到登入 —— cookie 過期本來就會這樣。 */
    }
    onOut();
  };

  return (
    <Shell>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-sm text-[var(--text-2)]">
          {me.company_name} · {me.email}
        </p>
        <Button size="sm" onClick={() => void logout()}>
          登出
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">目前沒有需要您確認的報價單。</p>
      ) : (
        <ul aria-label="您的報價單" className="m-0 flex flex-col gap-3 p-0">
          {rows.map((row) => (
            <li
              key={row.id}
              className="list-none rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="m-0 text-base font-semibold text-[var(--text)]">{row.title}</p>
                  <p className="m-0 text-sm text-[var(--text-3)]">
                    {row.quote_no} · {row.quote_date}
                  </p>
                </div>
                <div className="text-right">
                  <p className="m-0 text-lg font-semibold tabular-nums text-[var(--text)]">
                    {money(row.total)}
                  </p>
                  {/* 狀態的中文字樣由後端給。 */}
                  <Badge tone={row.signed_at ? "ok" : "neutral"}>{row.status_label}</Badge>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a
                  href={`${API_BASE}/client/quotes/${row.id}/pdf`}
                  className="inline-flex h-9 items-center rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-3.5 text-sm font-medium text-[var(--text)] hover:bg-[var(--surface-2)]"
                >
                  下載 PDF
                </a>
                {row.signed_at ? (
                  // 已經簽過的就不要再給一顆按鈕 —— 按下去只會得到一句 409。
                  <span className="text-sm text-[var(--ok)]">
                    已於 {row.signed_at.slice(0, 10)} 由 {row.signer_name} 線上簽署
                  </span>
                ) : row.can_sign ? (
                  <Button variant="primary" onClick={() => setSigning(row)}>
                    線上簽署
                  </Button>
                ) : (
                  <span className="text-sm text-[var(--text-3)]">
                    這份目前不開放線上簽署,請與承辦同仁聯絡
                  </span>
                )}
              </div>

              <p className="m-0 mt-2 text-xs text-[var(--text-3)]">
                也可以列印後簽名蓋章,把整張掃描回傳給承辦同仁,由我們代為上傳。
              </p>
            </li>
          ))}
        </ul>
      )}

      {signing ? (
        <SignForm
          quote={signing}
          onClose={() => setSigning(null)}
          onDone={() => {
            setSigning(null);
            refresh();
          }}
        />
      ) : null}
    </Shell>
  );
}

function SignForm({
  quote,
  onClose,
  onDone,
}: {
  quote: ClientQuote;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/client/quotes/${quote.id}/sign`, { signer_name: name });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "簽不下去");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      aria-label={`線上簽署 ${quote.quote_no}`}
      className="flex flex-col gap-3 rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--surface)] p-4"
    >
      <p className="m-0 text-sm text-[var(--text-2)]">
        您正在確認 <strong>{quote.quote_no}</strong>({quote.title}),
        金額 <strong className="tabular-nums">{money(quote.total)}</strong>。
      </p>
      <Input
        label="簽署人姓名"
        /* **要看得出是誰簽的。** 帳號的 Email 不夠 —— 那是窗口的信箱,
           而按下去的可能是他主管。 */
        hint="請填實際確認這份報價單的人"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
      <p className="m-0 text-xs text-[var(--text-3)]">
        按下確認後,系統會記錄您確認的是這一版報價單的內容與金額。
      </p>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>取消</Button>
        <Button type="submit" variant="primary" loading={busy} disabled={!name.trim()}>
          確認並簽署
        </Button>
      </div>
    </form>
  );
}
